import React from 'react';
import HiresPage from '../components/hires/HiresPage';
import { readInitialHiresView } from '../constants/hiresViewTabs';

/**
 * MY HIRES — the caller's OWN hire lines, in the SELECTED department only.
 *
 * 🔑 **This file is a SCOPE DECLARATION, not a page.** The page body — views,
 * toolbar, search, actions, dialogs, table — is `components/hires/HiresPage.js`,
 * shared with `DepartmentHires.js` and `AdminHires.js` (owner, 2026-10-03:
 * *"lets do them all"*). Everything below is a difference this page really has
 * from the other two; a field that merely restates the shared default is not
 * here. Read `HiresPage.js`'s docblock for the rules that govern all three.
 *
 * ⚖️ **OWNER RULING, 2026-09-01 — MY HIRES SHOWS THE SELECTED DEPARTMENT ONLY**
 * (*"MyHires should ONLY be for the department I have currently selected
 * please"*; memory `myhires-selected-department-only`), and 2026-09-29,
 * impersonating a buyer: *"My hires should only be the hire that person has
 * ordered."* Both are carried by ONE request shape — self-scoped `?user_id=`,
 * which the server then confines to the active department
 * (`server/routes/hires.js#selfScopedDeptClause`). Until 2026-09-29 a
 * department-wide viewer sent NO `user_id` and got the whole department here;
 * Dept Hires is that view, My Hires never is.
 * ⚠️ A site admin too: with no active department the server's
 * `selfScopedDeptClause` answers only their DEPARTMENT-LESS own rows, so an
 * unseated admin's My Hires is usually empty — accepted by the ruling (Dept
 * Hires / Admin Hires stay the wide views). Pinned by §5 of
 * `MyHires.departmentScope.test.js`.
 */
export const MY_HIRES_SCOPE = Object.freeze({
  logLabel: 'MyHires',
  testId: 'my-hires-page',
  rootClassName: 'p-2.5 max-md:pt-0 max-w-full',
  contextLabel: 'My hires',
  actionsHeaderTitle: 'My Hires',
  loadingMessage: 'Loading hire data...',
  emptyTitle: 'You have no hires yet',
  emptyDescription: 'Once you submit an order with hire equipment, the hire lines will show up here.',

  /** An explicit saved choice wins; otherwise TABLE on desktop, KANBAN below 768px. */
  initialView: readInitialHiresView,

  /** A click on a hire CARD opens the HIRE in the Table (owner, 2026-09-27). */
  openHireFromCard: true,

  /**
   * 🔴 `false` — a SUBSET column set, REGARDLESS of `canViewDeptWide`, so the
   * Table is always `MY_HIRE_TABLE_COLUMNS` / `MY_HIRE_TABLE_COL_WIDTHS_KEY`
   * (and below `md` the phone subset and ITS own key). Root `CLAUDE.md`'s
   * localStorage section: *"a SUBSET column set needs its OWN key"*. The shared
   * body derives both from this one flag through `hireTableColumnModel`, so the
   * set and the key can no longer be mismatched by a typo.
   */
  showRequestedBy: false,

  /**
   * 🔴 FALSE — the phone money figures are hidden here (owner, 2026-10-01:
   * *"the individual likely doesn't care"*). Department Hires keeps them.
   */
  showPhoneSummary: false,

  /** The `CostSummary` banner is dropped on a PHONE in every view — on all three
   *  hires pages since the owner's 2026-10-03 rulings. */
  hideCostBannerOnPhone: true,

  // Below `md` a horizontal Gantt timeline does not work at 360px.
  hideGanttOnPhone: true,

  // ── Behaviour flags — the same on all three pages since the 2026-10-03 rulings ──
  poll: true,
  staleRequestGuard: true,
  submitGuard: true,
  throwOnCalendarError: true,
  holdRowAfterAction: true,

  alwaysQueryString: true,

  /**
   * EVERY caller sees only their OWN hires. `sfx_user_id` is read at CALL time
   * rather than closed over, which is why `resolveFetchKey` below exists.
   */
  buildQueryString: () => {
    const userId = localStorage.getItem('sfx_user_id');
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    return params.toString();
  },

  /**
   * 🪤 A department switch must refetch, and this page's query closes over
   * NOTHING — so the active department id is the effect's dependency, exactly
   * as this page's own `useEffect` carried `activeDepartment` before the
   * extraction.
   */
  resolveFetchKey: ({ activeDepartment }) => activeDepartment?.department_id ?? null,

  /** Never fails closed: an empty self-scoped list is a true statement. */
  resolveCanQuery: () => true,

  /**
   * 🚨 WAS `isAdmin || isBuyer` — `isBuyer` is the UNSCOPED `user_roles` row and
   * has no `isSupervisor` disjunct at all (root `CLAUDE.md`, "Role Checking":
   * use the per-department CAPABILITY, never `isBuyer`).
   *
   * `capabilitiesFor(activeDepartment?.department_id)` is EXACT, not a proxy.
   * Every item this page can list is self-scoped AND confined server-side to
   * `activeDepartment` (the 2026-09-01 ruling), so resolving the capability in
   * `activeDepartment` IS resolving it in the ITEM'S OWN department for every
   * non-admin listed row — matching the server's own per-mutation gate
   * (`requireDepartmentCapability(req, res, item.order_department_id,
   * 'canManageDepartmentOrders')`) exactly.
   *
   * (This used to carry a long caveat about the list spanning departments and
   * showing a control the server would refuse. That was real, reproduced, and
   * is now DISSOLVED by the ruling above rather than merely narrowed — see
   * `git log -p` on this file if the analysis is ever needed again.)
   */
  resolveCanManage: ({ isAdmin, activeDepartment, capabilitiesFor }) => isAdmin || Boolean(
    capabilitiesFor(activeDepartment?.department_id).canManageDepartmentOrders
  ),

  /**
   * May this caller see the DEPARTMENT-WIDE order surface? Since 2026-09-29 it
   * no longer decides whose hires this page requests (always the caller's own);
   * it picks only WHERE "View order" goes and which order surface a Table row
   * expands into.
   *
   * 🔑 **THE DESTINATION IS DECIDED BY THE VIEWER'S CAPABILITY, NEVER BY
   * WHETHER THEY RAISED THE ORDER.** Owner, 2026-09-01: *"always default to dep
   * hire for buyer and supervisors even if they placed the order"*. It is the
   * client mirror of `App.js`'s `DepartmentRoute` gate on `/department/orders`
   * (`capability = 'canViewDepartmentOrders'`), of `GET /api/orders/department`'s
   * own `activeDeptCanViewOrders`, and of the server's `mayViewDeptHires`.
   *
   * 🪤 `canViewDepartmentOrders` is deliberately a DIFFERENT capability from
   * `resolveCanManage`'s `canManageDepartmentOrders` above — this is a VIEW
   * question, that one a MANAGE question. Two different capability names a few
   * lines apart is deliberate, not a typo; do not "fix" it into one.
   *
   * 🔑 Both `DepartmentRoute` and `GET /api/orders/department` ALSO admit a
   * Stores-only caller (`allowStores && canManageStores`); this deliberately
   * does NOT, mirroring the server's `mayViewDeptHires`, which has no stores
   * surface — a Stores-only viewer gets the self-scoped view from the rest of
   * this page, so their hire link must not land somewhere the page never sends
   * them.
   *
   * A caller WITHOUT the capability — a buyer or supervisor viewing a
   * department they hold no capability in, and a plain crew member viewing
   * their own hire — lands on My Orders instead.
   */
  resolveDeptWide: ({ isAdmin, activeDepartment, capabilitiesFor }) => isAdmin || Boolean(
    capabilitiesFor(activeDepartment?.department_id).canViewDepartmentOrders
  ),

  viewOrderPath: (orderId, ctx) => (
    MY_HIRES_SCOPE.resolveDeptWide(ctx)
      ? `/department/orders?order=${orderId}`
      : `/orders?order=${orderId}`
  ),

  /**
   * 🔑 `null`, deliberately, and NOT stale. This listener accepts a
   * hires-changed event for ANY departmentId because `fetchData` itself is the
   * real department filter (it always re-fetches the caller's CURRENT active
   * department), so a change in a department the caller is not viewing costs
   * one extra no-op silent refetch, never a wrong result. Narrowing it would
   * save that one request, not fix a correctness gap.
   */
  resolveInvalidationDepartmentId: () => null,

  resolveNotifyDepartmentId: ({ activeDepartment }) => activeDepartment?.department_id,
});

function MyHires() {
  return <HiresPage scope={MY_HIRES_SCOPE} />;
}

export default MyHires;
